import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import CreatableReactSelect from "components/base/CreatableReactSelect";
import {
  CategoriaType,
  CriarProdutoDtoType,
  DepartamentoType,
  FabricanteType,
  PrincipioAtivoType,
} from "data/produto";
import { Card, Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { usePermissionModal } from "contexts/PermissionModalContext";

const fetchDepartamentos = async () => {
  const { data } = await axios.get("/api/produto/departamentos");
  return data
};

const fetchCategorias = async () => {
  const { data } = await axios.get("/api/produto/categorias");
  return data
};

const fetchFabricantes = async () => {
  const { data } = await axios.get("/api/produto/fabricantes");
  return data
};

const fetchPrincipioAtivo = async () => {
  const { data } = await axios.get("/api/produto/principiosAtivos");
  return data
};

const OrganizeFormCard = ({ className }: { className?: string }) => {
  const { getMutationErrorHandler } = usePermissionModal();

  const departamentosQuery = useQuery<DepartamentoType[]>({
    queryKey: ["departamentos", "produto"],
    queryFn: () => fetchDepartamentos(),
  });

  const departamentoMutation = useMutation<any, any, { descricao: string }>({
    mutationFn: (d: { descricao: string }) => {
      return axios.post("/api/departamento", d);
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para criar departamentos"
    ),
  });

  const fabricantesQuery = useQuery<FabricanteType[]>({
    queryKey: ["fabricantes"],
    queryFn: fetchFabricantes,
  });

  const fabricanteMutation = useMutation<any, any, { descricao: string }>({
    mutationFn: (d: { descricao: string }) => {
      return axios.post("/api/fabricante", d);
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para criar fabricantes"
    ),
  });

  const categoriasQuery = useQuery<CategoriaType[]>({
    queryKey: ["categorias"],
    queryFn: fetchCategorias,
  });

  const categoriaMutation = useMutation<any, any, { descricao: string }>({
    mutationFn: (d: { descricao: string }) => {
      return axios.post("/api/categoria", d);
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para criar categorias"
    ),
  });

  const principioAtivoQuery = useQuery<PrincipioAtivoType[]>({
    queryKey: ["principioAtivo"],
    queryFn: fetchPrincipioAtivo,
  });

  const principioAtivoMutation = useMutation<any, any, { descricao: string }>({
    mutationFn: (d: { descricao: string }) => {
      return axios.post("/api/principioAtivo", d);
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para criar princípios ativos"
    ),
  });

  const { watch, setValue } = useFormContext<CriarProdutoDtoType>();

  const departamentoSelecionado = departamentosQuery?.data
    ?.map((e) => ({ label: e.descricao, value: e.id }))
    .find((d) => d.value == watch("departamentoId"));

  const fabricanteSelecionado = fabricantesQuery?.data
    ?.map((e) => ({ label: e.descricao, value: e.id }))
    .find((d) => d.value == watch("fabricanteId"));

  const categoriasSelecionadas = categoriasQuery?.data
    ?.map((e) => ({ label: e.descricao, value: e.id }))
    .filter((d) => watch("idsCategorias")?.includes(d.value));

  const principioAtivoSelecionado = principioAtivoQuery?.data
    ?.map((e) => ({ label: e.descricao, value: e.id }))
    .find((d) => d.value == watch("principioAtivoId"));

  return (
    <Card className={className}>
      <Card.Body>
        <h4 className="mb-4">Organização</h4>
        <Row className="gx-3 gy-4">
          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Departamento</h5>
            </div>
            <CreatableReactSelect
              isClearable
              onCreateOption={(inputValue) => {
                departamentoMutation.mutate({ descricao: inputValue }, {
                  onSuccess: (e) => {
                    departamentosQuery.refetch();
                    // Access id from the Result pattern response
                    setValue("departamentoId", e.data.data.id);
                  }
                  // O tratamento de erro já está no getMutationErrorHandler
                });
              }}
              value={departamentoSelecionado}
              onChange={(value: any) => {
                setValue("departamentoId", value?.value);
              }}
              options={departamentosQuery.data?.map((d) => ({
                value: d.id,
                label: d.descricao,
              }))}
              placeholder="Selecionar departamento"
            />
          </Col>
          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Laboratório</h5>
            </div>
            <CreatableReactSelect
              isClearable
              onCreateOption={(inputValue) => {
                fabricanteMutation.mutate({ descricao: inputValue }, {
                  onSuccess: (e) => {
                    fabricantesQuery.refetch();
                    // Access id from the Result pattern response
                    setValue("fabricanteId", e.data.data.id);
                  }
                  // O tratamento de erro já está no getMutationErrorHandler
                });
              }}
              value={fabricanteSelecionado}
              onChange={(value: any) => {
                setValue("fabricanteId", value?.value);
              }}
              options={fabricantesQuery.data?.map((d) => ({
                value: d.id,
                label: d.descricao,
              }))}
              placeholder="Selecionar fabricante"
            />
          </Col>
          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Princípio Ativo</h5>
            </div>
            <CreatableReactSelect
              isClearable
              onCreateOption={(inputValue) => {
                principioAtivoMutation.mutate({ descricao: inputValue }, {
                  onSuccess: (e) => {
                    principioAtivoQuery.refetch();
                    // Access id from the Result pattern response
                    setValue("principioAtivoId", e.data.data.id);
                  }
                  // O tratamento de erro já está no getMutationErrorHandler
                });
              }}
              value={principioAtivoSelecionado}
              onChange={(value: any) => {
                setValue("principioAtivoId", value?.value);
              }}
              options={principioAtivoQuery.data?.map((d) => ({
                value: d.id,
                label: d.descricao,
              }))}
              placeholder="Selecionar princípio ativo"
            />
          </Col>
          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Categorias</h5>
              {/* <Link className="fw-bold fs-9" to="#!">
                Nova categoria
              </Link> */}
            </div>
            <CreatableReactSelect
              isClearable
              isMulti
              onCreateOption={(inputValue) => {
                categoriaMutation.mutate({ descricao: inputValue }, {
                  onSuccess: (e) => {
                    categoriasQuery.refetch();
                    setValue("idsCategorias", [
                      ...(watch("idsCategorias") || []),
                      e.data.data.id,
                    ]);
                  }
                  // O tratamento de erro já está no getMutationErrorHandler
                });
              }}
              value={categoriasSelecionadas}
              onChange={(value: any) => {
                setValue("idsCategorias", value?.map((v: any) => v.value));
              }}
              options={categoriasQuery.data?.map((d) => ({
                value: d.id,
                label: d.descricao,
              }))}
              placeholder="Selecionar categorias"
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default OrganizeFormCard;
